import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  urlAnalyzed?: string;
  keyword?: string;
}

type ErrorType = "elogio" | "critica" | "outros";

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export function FeedbackModal({ open, onOpenChange, urlAnalyzed, keyword }: FeedbackModalProps) {
  const [score, setScore] = useState<number | null>(null);
  const [errorType, setErrorType] = useState<ErrorType | "">("");
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!score) {
      toast.error("Por favor, selecione uma nota de 1 a 5");
      return;
    }

    if (!errorType) {
      toast.error("Por favor, selecione um tipo de feedback");
      return;
    }

    if (!message.trim()) {
      toast.error("Por favor, deixe uma mensagem com seu feedback");
      return;
    }

    if (!name.trim()) {
      toast.error("Por favor, informe seu nome");
      return;
    }

    if (!email.trim()) {
      toast.error("Por favor, informe seu e-mail");
      return;
    }

    if (!isValidEmail(email.trim())) {
      toast.error("Por favor, informe um e-mail válido");
      return;
    }

    setIsSubmitting(true);

    try {
      console.log("Enviando feedback via Supabase function");
      const { data, error } = await supabase.functions.invoke('feedback', {
        body: {
          name: name.trim(),
          email: email.trim(),
          score,
          error_type: errorType,
          message: message.trim(),
          url_analyzed: urlAnalyzed,
          keyword: keyword,
        },
      });

      if (error) {
        console.error("Erro ao enviar feedback:", error);
        throw error;
      }

      console.log("Resposta do feedback:", data);
      toast.success("Feedback enviado com sucesso! Obrigado pela sua contribuição.");
      onOpenChange(false);
      setMessage("");
      setScore(null);
      setErrorType("");
      setName("");
      setEmail("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Erro ao enviar feedback. Por favor, tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Enviar Feedback</DialogTitle>
          <DialogDescription>
            Nossa ferramenta está em beta! Ajude-nos a melhorar com seu feedback sincero.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Como você avalia sua experiência? (1-5)</Label>
            <div className="flex gap-4">
              {[1, 2, 3, 4, 5].map((value) => (
                <Button
                  key={value}
                  type="button"
                  variant={score === value ? "default" : "outline"}
                  className="w-10 h-10 p-0"
                  onClick={() => setScore(value)}
                >
                  {value}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
            />
          </div>

          <div className="space-y-2">
            <Label>Tipo de feedback</Label>
            <RadioGroup value={errorType} onValueChange={(value) => setErrorType(value as ErrorType)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="elogio" id="elogio" />
                <Label htmlFor="elogio">Elogio</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="critica" id="critica" />
                <Label htmlFor="critica">Crítica</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="outros" id="outros" />
                <Label htmlFor="outros">Outros</Label>
              </div>
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Mensagem</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Descreva sua experiência ou sugestão..."
              className="resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Enviando..." : "Enviar feedback"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}